import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  Heart, 
  BookOpen, 
  Sparkles, 
  Target,
  Briefcase,
  Calendar,
  Cross,
  ChevronRight,
  ChevronLeft,
  Loader2,
  Check,
  Star
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import StepReviewModal from '../components/StepReviewModal';
import VoiceInput from '../components/VoiceInput';

const stepIcons = {
  1: Heart,
  2: BookOpen,
  3: Sparkles,
  4: Target,
  5: Briefcase,
  6: Calendar,
  7: Cross
};

const VisionSetup = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [prompts, setPrompts] = useState(null);
  const [formData, setFormData] = useState({});
  const [completedSteps, setCompletedSteps] = useState([]);
  const [visionResult, setVisionResult] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [hasSeenResults, setHasSeenResults] = useState(false);

  useEffect(() => {
    fetchPromptsAndStatus();
  }, [i18n.language]); // Reload when language changes

  const fetchPromptsAndStatus = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Fetch prompts in user's language
      const currentLang = i18n.language || 'en';
      const promptsRes = await axios.get(`${API}/vision-setup/prompts?lang=${currentLang}`, { headers });
      setPrompts(promptsRes.data.prompts);
      
      // Fetch status
      const statusRes = await axios.get(`${API}/vision-setup/status`, { headers });
      if (statusRes.data.completed) {
        setVisionResult({
          vision_statement: statusRes.data.vision_statement,
          tagline: statusRes.data.data?.tagline,
          core_values: statusRes.data.data?.core_values,
          brand_essence: statusRes.data.data?.brand_essence,
          affirmation: statusRes.data.data?.affirmation
        });
      }
      setCurrentStep(statusRes.data.current_step || 1);
      setCompletedSteps(statusRes.data.steps_completed || []);
      
      // Load existing data
      if (statusRes.data.data) {
        const existingData = {};
        for (let i = 1; i <= 7; i++) {
          if (statusRes.data.data[`step${i}`]) {
            existingData[i] = statusRes.data.data[`step${i}`];
          }
        }
        setFormData(existingData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (step, key, value) => {
    setFormData(prev => ({
      ...prev,
      [step]: {
        ...prev[step],
        [key]: value
      }
    }));
  };

  const saveStep = async () => {
    setSaving(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/vision-setup/step/${currentStep}`, formData[currentStep] || {}, { headers });
      
      if (!completedSteps.includes(currentStep)) {
        setCompletedSteps([...completedSteps, currentStep]);
      }
      
      toast.success(`Step ${currentStep} saved`);
      return true;
    } catch (error) {
      toast.error('Failed to save');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    const saved = await saveStep();
    if (saved && currentStep < 7) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    const saved = await saveStep();
    if (!saved) return;
    
    setCompleting(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/vision-setup/complete`, {}, { headers });
      setVisionResult(response.data);
      setHasSeenResults(false); // User hasn't seen results yet
      toast.success('Ta vision est maintenant claire !');
      // Review will be shown after user clicks "J'ai compris" button
    } catch (error) {
      toast.error('Une erreur est survenue');
    } finally {
      setCompleting(false);
    }
  };

  const handleReviewValidated = () => {
    setShowReviewModal(false);
    // User can now see the result and continue
  };

  const goToDashboard = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#D4AF37]" size={48} />
      </div>
    );
  }

  // Show Vision Result if completed
  if (visionResult) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-[#D4AF37]/20 to-transparent flex items-center justify-center">
              <Check className="text-[#D4AF37]" size={40} />
            </div>
            <h1 className="font-serif text-4xl text-white mb-4">Your Vision is Ready</h1>
            <p className="text-[#A1A1AA]">This is the foundation of everything you're about to build.</p>
          </motion.div>

          {/* Vision Statement */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="card-premium p-8 mb-6 bg-gradient-to-br from-[#D4AF37]/10 to-transparent"
          >
            <Star className="text-[#D4AF37] mb-4" size={24} />
            <p className="text-[#E5C585] text-xs font-accent tracking-widest mb-4">YOUR VISION STATEMENT</p>
            <p className="text-white font-serif text-xl leading-relaxed italic">
              "{visionResult.vision_statement}"
            </p>
          </motion.div>

          {/* Tagline */}
          {visionResult.tagline && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="card-premium p-6 mb-6 text-center"
            >
              <p className="text-[#E5C585] text-xs font-accent tracking-widest mb-2">YOUR TAGLINE</p>
              <p className="text-white text-lg">{visionResult.tagline}</p>
            </motion.div>
          )}

          {/* Core Values & Brand Essence */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {visionResult.core_values && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="card-premium p-6"
              >
                <p className="text-[#E5C585] text-xs font-accent tracking-widest mb-4">CORE VALUES</p>
                <div className="flex flex-wrap gap-2">
                  {visionResult.core_values.map((value, i) => (
                    <span key={i} className="px-4 py-2 bg-[#1C1C1C] text-[#D4AF37] text-sm border border-[#D4AF37]/20">
                      {value}
                    </span>
                  ))}
                </div>
              </motion.div>
            )}

            {visionResult.brand_essence && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                className="card-premium p-6 text-center"
              >
                <p className="text-[#E5C585] text-xs font-accent tracking-widest mb-4">BRAND ESSENCE</p>
                <p className="text-[#D4AF37] font-serif text-3xl">{visionResult.brand_essence}</p>
              </motion.div>
            )}
          </div>

          {/* Affirmation */}
          {visionResult.affirmation && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="card-premium p-6 mb-8"
            >
              <p className="text-[#E5C585] text-xs font-accent tracking-widest mb-3">DAILY AFFIRMATION</p>
              <p className="text-[#A1A1AA] italic">"{visionResult.affirmation}"</p>
            </motion.div>
          )}

          {/* Call to Action - Triggers Review */}
          {!hasSeenResults && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              className="card-gold p-6 text-center mb-6"
            >
              <p className="text-white mb-4">As-tu pris le temps de découvrir ta vision ?</p>
              <Button
                onClick={() => {
                  setHasSeenResults(true);
                  setShowReviewModal(true);
                }}
                className="btn-gold px-8 py-3"
                data-testid="understood-btn"
              >
                J'ai compris, donner mon avis ✨
              </Button>
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: hasSeenResults ? 0.35 : 0.45 }}
            className="text-center"
          >
            <Button onClick={goToDashboard} className="btn-gold px-12 py-4" data-testid="go-to-dashboard">
              Aller au Dashboard
              <ChevronRight className="ml-2" size={18} />
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  const currentPrompt = prompts?.[`step${currentStep}`];
  const StepIcon = stepIcons[currentStep];

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Progress */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-4">
            <p className="text-[#D4AF37] text-xs font-accent tracking-widest">VISION SETUP</p>
            <p className="text-[#52525B] text-sm">Step {currentStep} of 7</p>
          </div>
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5, 6, 7].map((step) => (
              <button
                key={step}
                onClick={() => completedSteps.includes(step) || step <= currentStep ? setCurrentStep(step) : null}
                className={`flex-1 h-1 transition-colors ${
                  step === currentStep
                    ? 'bg-[#D4AF37]'
                    : completedSteps.includes(step)
                    ? 'bg-[#D4AF37]/50'
                    : 'bg-[#27272A]'
                } ${completedSteps.includes(step) || step <= currentStep ? 'cursor-pointer hover:bg-[#D4AF37]/70' : ''}`}
              />
            ))}
          </div>
        </div>

        {/* Step Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {/* Step Header */}
            <div className="text-center mb-10">
              <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-[#D4AF37]/20 to-transparent flex items-center justify-center">
                <StepIcon className="text-[#D4AF37]" size={28} />
              </div>
              <h1 className="font-serif text-3xl text-white mb-4">{currentPrompt?.title}</h1>
              
              {/* Special intro for Step 3 */}
              {currentStep === 3 && currentPrompt?.intro_fr && (
                <div className="card-premium p-8 mb-8 text-left max-w-2xl mx-auto">
                  <p className="text-[#A1A1AA] whitespace-pre-line leading-relaxed italic">
                    {currentPrompt.intro_fr}
                  </p>
                </div>
              )}
              
              {/* Regular intro */}
              {currentPrompt?.intro && currentStep !== 3 && (
                <p className="text-[#A1A1AA] max-w-lg mx-auto">{currentPrompt.intro}</p>
              )}
            </div>

            {/* Faith Foundation - Bible Verse (Step 7) */}
            {currentStep === 7 && currentPrompt?.verse && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="card-premium p-8 mb-8 bg-gradient-to-br from-[#D4AF37]/5 to-transparent text-center"
              >
                <Cross className="text-[#D4AF37] mx-auto mb-4" size={24} />
                <p className="text-[#D4AF37] font-accent text-xs tracking-widest mb-3">
                  {currentPrompt.verse.reference}
                </p>
                <p className="text-white font-serif text-xl italic mb-4">
                  "{currentPrompt.verse.text}"
                </p>
                <p className="text-[#A1A1AA] text-sm leading-relaxed max-w-lg mx-auto">
                  {currentPrompt.verse.reflection}
                </p>
              </motion.div>
            )}

            {/* Questions */}
            <div className="space-y-8">
              {currentPrompt?.questions?.map((q, index) => (
                <motion.div
                  key={q.key}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="card-premium p-6"
                >
                  <label className="block text-white font-serif text-lg mb-4">
                    {q.question}
                  </label>
                  {q.key === 'key_goals' ? (
                    <Input
                      value={formData[currentStep]?.[q.key] || ''}
                      onChange={(e) => handleInputChange(currentStep, q.key, e.target.value)}
                      placeholder={q.placeholder}
                      className="bg-transparent border-b border-[#27272A] focus:border-[#D4AF37] rounded-none px-0 py-3 text-white placeholder:text-[#52525B]"
                    />
                  ) : (
                    <div className="relative">
                      <Textarea
                        value={formData[currentStep]?.[q.key] || ''}
                        onChange={(e) => handleInputChange(currentStep, q.key, e.target.value)}
                        placeholder={q.placeholder}
                        className="bg-transparent border border-[#27272A] focus:border-[#D4AF37] rounded-none p-4 pr-14 min-h-[120px] text-white placeholder:text-[#52525B] resize-none"
                      />
                      {/* Voice input button */}
                      <div className="absolute right-3 bottom-3">
                        <VoiceInput 
                          onTranscription={(text) => {
                            const currentValue = formData[currentStep]?.[q.key] || '';
                            const newValue = currentValue ? `${currentValue} ${text}` : text;
                            handleInputChange(currentStep, q.key, newValue);
                          }}
                        />
                      </div>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>

            {/* Navigation */}
            <div className="flex justify-between mt-12">
              <Button
                variant="outline"
                onClick={handlePrev}
                disabled={currentStep === 1}
                className="border-[#27272A] text-[#A1A1AA] hover:border-[#D4AF37] hover:text-white px-6 py-3"
              >
                <ChevronLeft className="mr-2" size={18} />
                Previous
              </Button>

              {currentStep < 7 ? (
                <Button
                  onClick={handleNext}
                  disabled={saving}
                  className="btn-gold px-8 py-3"
                  data-testid="next-step"
                >
                  {saving ? (
                    <Loader2 className="animate-spin mr-2" size={18} />
                  ) : null}
                  Save & Continue
                  <ChevronRight className="ml-2" size={18} />
                </Button>
              ) : (
                <Button
                  onClick={handleComplete}
                  disabled={completing}
                  className="btn-gold px-8 py-3"
                  data-testid="complete-vision"
                >
                  {completing ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={18} />
                      Creating Your Vision...
                    </>
                  ) : (
                    <>
                      Complete Vision Setup
                      <Sparkles className="ml-2" size={18} />
                    </>
                  )}
                </Button>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Step Review Modal */}
      <StepReviewModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        stepId="vision_revelation"
        stepName="Révéler ta vision"
        onValidated={handleReviewValidated}
      />
    </div>
  );
};

export default VisionSetup;
